import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Box, Typography, Paper, InputLabel, Select, MenuItem,
} from '@mui/material';
import DnsIcon from '@mui/icons-material/Dns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { fetchRequest as addWorkspace, clearState as clearWorkspaceState } from '../../redux/actions/workspaces/create';
import { fetchRequest as getUser } from '../../redux/actions/user/getUser';
import {
  CMButton, CMFormControl, CMButtonOutlinedSmall,
  CMSmallTextField, CMSmallChipsField,
} from '../../containers/styledComponents';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { format } from '../../utils/datetime';

// ISO expiry for a lifetime choice ("permanent" -> null, "7" -> now + 7d).
const expiresAtFor = (lifetime) => {
    const days = Number(lifetime);
    if (!Number.isFinite(days) || days <= 0) return null;
    return new Date(Date.now() + days * 86_400_000).toISOString();
};

const safeFormatDate = (d, fmt = 'PPp') => {
    try { return d ? format(new Date(d), fmt) : '—'; } catch (_e) { return '—'; }
};

/**
 * Workspace creation - user-driven (no AI generation).
 *
 * Only the workspace NAME is required. The "Goal" (what the user is trying to
 * predict / achieve) plus description, sectors, and keywords are optional but
 * recommended - the Goal in particular is fed to the insights generator and
 * the AI segment suggestion so they tailor results to it.
 */
const WorkspaceCreatePage = (({ createLoading, workspaceAdd, createPayload, createError, clearStateWorkspace, getUser }) => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [jsonData, setJsonData] = useState({
        name: "",
        goal: "",
        description: "",
        keywords: [],
        sectors: [],
        data_granularity: "day",
    });
    // "permanent" or a number of days; a number makes the workspace ephemeral.
    const [lifetime, setLifetime] = useState('permanent');

    useEffect(() => {
        if (createPayload != null) {
            enqueueSnackbar('Workspace created successfully', { variant: 'success' });
            // Select the freshly-created workspace so the rest of the app is
            // scoped to it (prevents the "Please select a workspace" loop for
            // first-time users). Hard-navigate to the dashboard so every
            // component mounts with the new workspace id (mirrors the Header's
            // workspace switch).
            const newId = createPayload.uid || createPayload.workspace_id;
            clearStateWorkspace();
            if (newId) {
                try { localStorage.setItem('WORKSPACE_ID', newId); } catch (_) { /* ignore */ }
                window.location.assign('/');
                return;
            }
            getUser();
            navigate('/workspaces');
        }
        // NOTE: We intentionally do NOT raise a toast here on ``createError``.
        // The create saga already dispatches the global ``Error`` action, which
        // the top-level <Error /> handler turns into a single, specific toast
        // (e.g. the backend's "Workspace limit exceeded…" message). Showing a
        // second generic "Error creating workspace" toast here would duplicate it.
    }, [createPayload, createError, clearStateWorkspace, navigate, enqueueSnackbar, getUser]);

    const setField = (field) => (value) => setJsonData((prev) => ({ ...prev, [field]: value }));

    const handleBack = () => navigate('/workspaces');

    const handleSubmit = (event) => {
        event.preventDefault();
        if (jsonData.name.trim() === "") {
            return enqueueSnackbar('Workspace name is required', { variant: 'error' });
        }
        // Goal is stored under the existing ``purpose`` key for backend
        // compatibility; everything except the name is optional.
        const expiresAt = expiresAtFor(lifetime);
        workspaceAdd({
            ...jsonData,
            purpose: jsonData.goal,
            data_granularity: jsonData.data_granularity || 'day',
            ...(expiresAt ? { expires_at: expiresAt } : {}),
        });
    };

    return (
        <Box sx={{ p: 0, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <PageBreadcrumbs
                    items={[
                        { text: "Workspaces", icon: <DnsIcon />, link: "/workspaces" },
                        { text: "Create new workspace" },
                    ]}
                />
                <CMButtonOutlinedSmall
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    color="primary"
                    onClick={handleBack}
                    size="small"
                >
                    Back
                </CMButtonOutlinedSmall>
            </Box>

            <Paper sx={{ p: 3, mb: 4, width: '80%', minWidth: '500px' }}>
                <form onSubmit={handleSubmit}>
                    <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 600 }}>
                        Workspace details
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Only a name is required. Adding a clear goal helps the AI tailor
                        insights and segment suggestions to what you're trying to predict.
                    </Typography>

                    <CMSmallTextField
                        label="Name"
                        required
                        value={jsonData.name}
                        onChange={(e) => setField('name')(e.target.value)}
                        fullWidth
                        margin="dense"
                        placeholder="e.g. Retail demand forecasting"
                    />

                    <CMSmallTextField
                        label="Goal (optional)"
                        value={jsonData.goal}
                        onChange={(e) => setField('goal')(e.target.value)}
                        fullWidth
                        margin="dense"
                        multiline
                        minRows={2}
                        placeholder="What are you trying to predict or achieve? e.g. Forecast next-week unit sales per store."
                    />

                    <CMSmallTextField
                        label="Description (optional)"
                        value={jsonData.description}
                        onChange={(e) => setField('description')(e.target.value)}
                        fullWidth
                        margin="dense"
                        multiline
                        minRows={2}
                        placeholder="Background, data sources, anything useful for context."
                    />

                    <Box sx={{ mt: 1, mb: 1 }}>
                        <CMSmallChipsField
                            label="Sectors (optional)"
                            value={jsonData.sectors}
                            onChange={setField('sectors')}
                            sx={{ width: '100%', fontSize: 14 }}
                        />
                    </Box>

                    <Box sx={{ mt: 1, mb: 1 }}>
                        <CMSmallChipsField
                            label="Keywords (optional)"
                            value={jsonData.keywords}
                            onChange={setField('keywords')}
                            sx={{ width: '100%', fontSize: 14 }}
                        />
                    </Box>

                    <CMFormControl fullWidth margin="normal">
                        <InputLabel id="granularity-label">Data Granularity</InputLabel>
                        <Select
                            labelId="granularity-label"
                            value={jsonData.data_granularity}
                            label="Data Granularity"
                            onChange={(e) => setField('data_granularity')(e.target.value)}
                        >
                            <MenuItem value="second">Second</MenuItem>
                            <MenuItem value="minute">Minute</MenuItem>
                            <MenuItem value="hour">Hourly</MenuItem>
                            <MenuItem value="day">Daily</MenuItem>
                            <MenuItem value="week">Weekly</MenuItem>
                            <MenuItem value="month">Monthly</MenuItem>
                            <MenuItem value="year">Yearly</MenuItem>
                        </Select>
                    </CMFormControl>

                    <CMFormControl fullWidth margin="normal">
                        <InputLabel id="lifetime-label">Keep this workspace</InputLabel>
                        <Select
                            labelId="lifetime-label"
                            value={lifetime}
                            label="Keep this workspace"
                            onChange={(e) => setLifetime(e.target.value)}
                        >
                            <MenuItem value="permanent">Permanently</MenuItem>
                            <MenuItem value="7">For 7 days</MenuItem>
                            <MenuItem value="30">For 30 days</MenuItem>
                            <MenuItem value="90">For 90 days</MenuItem>
                        </Select>
                    </CMFormControl>
                    {lifetime !== 'permanent' && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: -0.5, mb: 1 }}>
                            Ephemeral workspace: on {safeFormatDate(expiresAtFor(lifetime))} it is deleted with
                            everything in it. You can keep it (make it permanent) from workspace settings at any time.
                        </Typography>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <CMButton
                            startIcon={<AddCircleIcon />}
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={createLoading}
                        >
                            Create workspace
                        </CMButton>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
});

const mapStateToProps = (state) => ({
    createLoading: state.workspace_create.isLoading,
    createPayload: state.workspace_create.response,
    createError: state.workspace_create.error,
});

const mapDispatchToProps = (dispatch) => ({
    workspaceAdd: (json) => dispatch(addWorkspace({ json })),
    clearStateWorkspace: () => dispatch(clearWorkspaceState()),
    getUser: () => dispatch(getUser()),
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkspaceCreatePage);
